<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所开庭计划：登记 → 完成/取消。仅本所 Matter。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="已排期" value="SCHEDULED" />
          <el-option label="已完成" value="COMPLETED" />
          <el-option label="已取消" value="CANCELLED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">登记开庭</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="120" />
      <el-table-column prop="caseTitle" label="案件" min-width="140" show-overflow-tooltip />
      <el-table-column prop="hearingTime" label="开庭时间" width="170" />
      <el-table-column prop="location" label="地点" min-width="100" show-overflow-tooltip />
      <el-table-column prop="courtRoom" label="法庭" width="90" />
      <el-table-column prop="judgeName" label="法官" width="90" />
      <el-table-column prop="status" label="状态" width="90" />
      <el-table-column prop="result" label="结果" min-width="100" show-overflow-tooltip />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <template v-if="row.status === 'SCHEDULED'">
            <el-button link type="success" @click="doComplete(row)">完成</el-button>
            <el-button link type="danger" @click="doCancel(row)">取消</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="登记开庭" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%">
            <el-option
              v-for="m in matterOptions"
              :key="m.id"
              :label="`${m.firmCaseNo || m.id} · ${m.title}`"
              :value="m.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="开庭时间" prop="hearingTime">
          <el-date-picker
            v-model="form.hearingTime"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            class="!w-100%"
          />
        </el-form-item>
        <el-form-item label="地点">
          <el-input v-model="form.location" />
        </el-form-item>
        <el-form-item label="法庭">
          <el-input v-model="form.courtRoom" />
        </el-form-item>
        <el-form-item label="法官">
          <el-input v-model="form.judgeName" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCreate">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import {
  FirmPracticeApi,
  type PracticeHearingVO,
  type PracticeMatterVO
} from '@/api/firm/practice'

defineOptions({ name: 'FirmPracticeHearing' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeHearingVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  caseId: undefined as number | undefined,
  status: undefined as string | undefined
})

const matterOptions = ref<PracticeMatterVO[]>([])
const createVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  caseId: undefined as number | undefined,
  hearingTime: '',
  location: '',
  courtRoom: '',
  judgeName: '',
  remark: ''
})
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  hearingTime: [{ required: true, message: '请选择开庭时间', trigger: 'change' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.hearingPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const openCreate = async () => {
  form.caseId = undefined
  form.hearingTime = ''
  form.location = ''
  form.courtRoom = ''
  form.judgeName = ''
  form.remark = ''
  createVisible.value = true
  const matters = await FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 })
  matterOptions.value = matters?.list || []
}

const submitCreate = async () => {
  await formRef.value?.validate()
  if (!form.caseId) return
  saving.value = true
  try {
    await FirmPracticeApi.hearingCreate({
      caseId: form.caseId,
      hearingTime: form.hearingTime,
      location: form.location || undefined,
      courtRoom: form.courtRoom || undefined,
      judgeName: form.judgeName || undefined,
      remark: form.remark || undefined
    })
    message.success('已登记')
    createVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const doComplete = async (row: PracticeHearingVO) => {
  if (!row.id) return
  const result = window.prompt('开庭结果（可选）') ?? undefined
  await FirmPracticeApi.hearingComplete({ id: row.id, result: result || undefined })
  message.success('已完成')
  await getList()
}

const doCancel = async (row: PracticeHearingVO) => {
  if (!row.id) return
  try {
    await message.confirm('确认取消该开庭？')
  } catch {
    return
  }
  await FirmPracticeApi.hearingCancel(row.id)
  message.success('已取消')
  await getList()
}

onMounted(getList)
</script>
